import { cva } from 'class-variance-authority';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { XStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { DropdownMenu } from '@/components/ui/dropdown-menu';

import { useTranscriptionInputDevice } from './transcription-input-device.hooks';
import {
  fromOptionValue,
  SYSTEM_DEFAULT_OPTION_VALUE,
} from './transcription-input-device.options';

import type { TranscriptionInputDeviceConfig } from './transcription-input-device.types';

const LOADING_LABEL = '—';
const SYSTEM_DEFAULT_LABEL = 'System Default';

const buttonVariants = cva('group', {
  variants: {
    variant: {
      default: null,
      translucent: 'hover:bg-grayA-2 data-[state=open]:bg-grayA-2',
      compact:
        'p-0 !bg-transparent !hover:bg-transparent !text-foreground-subtle hover:!text-foreground',
    },
  },
  defaultVariants: { variant: 'default' },
});

const triggerVariants = cva('items-center', {
  variants: {
    size: {
      default: 'gap-1.5',
      sm: 'gap-1',
    },
  },
  defaultVariants: { size: 'default' },
});

const iconVariants = cva('', {
  variants: {
    size: {
      default: '!size-4',
      sm: '!size-3.5',
    },
  },
  defaultVariants: { size: 'default' },
});

const labelVariants = cva('truncate py-1', {
  variants: {
    size: {
      default: 'max-w-[160px]',
      sm: 'max-w-[140px] text-xs.5',
    },
  },
  defaultVariants: { size: 'default' },
});

const chevronVariants = cva(
  'text-foreground-muted transition-transform duration-200 group-data-[state=open]:rotate-180',
  {
    variants: {
      size: {
        default: '!size-3',
        sm: '!size-2.5',
      },
    },
    defaultVariants: { size: 'default' },
  }
);

const iconStrokeWidth = { default: 1.25, sm: 1.5 } as const;
const buttonSizeMap = { default: 'sm', sm: 'xs' } as const;

interface TranscriptionInputDeviceDropdownProps {
  config: TranscriptionInputDeviceConfig;
  size?: 'default' | 'sm';
  variant?: 'default' | 'compact' | 'translucent';
  availableOnly?: boolean;
  align?: 'start' | 'end';
}

export const TranscriptionInputDeviceDropdown = observer(
  function TranscriptionInputDeviceDropdown({
    config,
    size = 'default',
    variant = 'default',
    availableOnly = false,
    align = 'start',
  }: TranscriptionInputDeviceDropdownProps) {
    const {
      state,
      displayName,
      getDevices,
      isLoaded,
      selectDevice,
      selectedValue,
    } = useTranscriptionInputDevice(config);

    const visibleDevices = state
      ? availableOnly
        ? state.availableDevices.filter((device) => device.isAvailable)
        : state.availableDevices
      : [];
    const hasDevices = visibleDevices.length > 0;
    const shouldShowSystemDefaultOption = !availableOnly;
    // The available-only variant is used by the silent-input notification.
    // It intentionally shows only alternative devices, so nothing is preselected.
    const radioValue = shouldShowSystemDefaultOption ? selectedValue : '';

    return (
      <DropdownMenu.Root
        modal={false}
        onOpenChange={(open) => {
          if (open) {
            getDevices();
          }
        }}
      >
        <DropdownMenu.Trigger asChild>
          <Button
            variant="ghost"
            size={buttonSizeMap[size]}
            className={buttonVariants({ variant })}
          >
            <XStack className={triggerVariants({ size })}>
              <Icons.recordAudio
                className={iconVariants({ size })}
                strokeWidth={iconStrokeWidth[size]}
              />
              <span className={labelVariants({ size })}>{displayName}</span>
              <Icons.chevronDown className={chevronVariants({ size })} />
            </XStack>
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align={align} sideOffset={8} className="w-64">
          <DropdownMenu.Label>Input Device</DropdownMenu.Label>
          <DropdownMenu.Separator />
          {!isLoaded ? (
            <DropdownMenu.Item disabled>{LOADING_LABEL}</DropdownMenu.Item>
          ) : !hasDevices ? (
            <DropdownMenu.Item disabled>No devices available</DropdownMenu.Item>
          ) : (
            <DropdownMenu.RadioGroup
              value={radioValue}
              onValueChange={(value) =>
                selectDevice(
                  shouldShowSystemDefaultOption
                    ? fromOptionValue(value)
                    : { type: 'specific_device', deviceId: value }
                )
              }
            >
              {shouldShowSystemDefaultOption && (
                <>
                  <DropdownMenu.RadioItem value={SYSTEM_DEFAULT_OPTION_VALUE}>
                    <span className="truncate px-2">
                      {SYSTEM_DEFAULT_LABEL}
                    </span>
                  </DropdownMenu.RadioItem>
                  <DropdownMenu.Separator />
                </>
              )}
              {visibleDevices.map((device) => (
                <DropdownMenu.RadioItem key={device.id} value={device.id}>
                  <span className="truncate px-2">{device.name}</span>
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
);
